import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export type UIStateType = {
  showImportGames: boolean;
  gameIdForModal: number | null;
  // Opens the game overlay at this move (pgn). Separate from the filter `pgn` param so it doesn't
  // re-filter the list. Cleared when the game changes.
  movePgnForModal: string | null;
  // A shareable deep-link move reference, `<number><w|b>` (e.g. "12w"), resolved against the game's
  // moves; also opens the comment panel.
  moveRefForModal: string | null;
  // Open the game straight into practice, from the list's correction card. Consumed once.
  openCorrectForModal: boolean;
  // With openCorrectForModal: the mistake to practise (move id) from a deep link
  // (?gameId=…&practice=<moveId>, the "your game is ready" email). Null = the worst one.
  practiceMoveIdForModal: number | null;
  // Bumped to reload the open game; re-dispatching the same gameId wouldn't re-render.
  gameRefreshKey: number;
  // The "Manage groups" modal (create/rename/visibility/delete).
  showManageGroups: boolean;
  // Bulk-selection mode on the games list, and the games currently ticked.
  selectionMode: boolean;
  selectedGameIds: number[];
  // The cross-game repair session. Null = closed; an empty array means the games on screen.
  repairGameIds: number[] | null;
  // The depth the session opens at (4 blunders … 1 inaccuracies). Null = blunders. /try sets it so a
  // visitor with no blunders doesn't land on an empty queue.
  repairMinWeight: number | null;
  // "Play it out": a position from the viewer's own game, handed to the engine. Null = closed. Carries
  // the position rather than a move id, since the repair session and the game overlay both open it.
  playOut: PlayOutRequest | null;
};

export type PlayOutRequest = {
  fen: string;
  // The viewer's own colour in the game the position came from.
  color: 'white' | 'black';
  // Stored evaluation (white centipawns), so the board can show what it was worth. Null if unevaluated.
  evaluationCp: number | null;
  // One line of header context (whose game, which move), built by the caller.
  label: string | null;
};

export const UIInitialState: UIStateType = {
  showImportGames: false,
  gameIdForModal: null,
  movePgnForModal: null,
  moveRefForModal: null,
  openCorrectForModal: false,
  practiceMoveIdForModal: null,
  gameRefreshKey: 0,
  showManageGroups: false,
  selectionMode: false,
  selectedGameIds: [],
  repairGameIds: null,
  repairMinWeight: null,
  playOut: null,
};

const slice = createSlice({
  name: 'UI',
  initialState: UIInitialState,
  reducers: {
    setGameIdForModal: (state, action: PayloadAction<number | null>) => {
      state.gameIdForModal = action.payload;
      // Normal opens and prev/next start at the default position.
      state.movePgnForModal = null;
      state.moveRefForModal = null;
      state.openCorrectForModal = false;
      state.practiceMoveIdForModal = null;
    },
    // The game overlay clears the auto-open flag once it has acted on it.
    clearOpenCorrect: (state) => {
      state.openCorrectForModal = false;
      state.practiceMoveIdForModal = null;
    },
    setMoveRefForModal: (state, action: PayloadAction<string | null>) => {
      state.moveRefForModal = action.payload;
    },
    refreshGameForModal: (state) => {
      state.gameRefreshKey += 1;
    },
    setShowImportGames: (state, action: PayloadAction<boolean>) => {
      state.showImportGames = action.payload;
    },
    setShowManageGroups: (state, action: PayloadAction<boolean>) => {
      state.showManageGroups = action.payload;
    },
    setSelectionMode: (state, action: PayloadAction<boolean>) => {
      state.selectionMode = action.payload;
      // Leaving selection mode clears the ticks.
      if (!action.payload) {
        state.selectedGameIds = [];
      }
    },
    toggleGameSelected: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      state.selectedGameIds = state.selectedGameIds.includes(id)
        ? state.selectedGameIds.filter((x) => x !== id)
        : [...state.selectedGameIds, id];
    },
    // Replace the whole tick set ("select all").
    setSelectedGameIds: (state, action: PayloadAction<number[]>) => {
      state.selectedGameIds = action.payload;
    },
    clearSelection: (state) => {
      state.selectedGameIds = [];
    },
    // Open the cross-game repair session at a depth the caller knows holds a mistake.
    openRepairAt: (
      state,
      action: PayloadAction<{ gameIds: number[]; minWeight: number }>,
    ) => {
      state.repairGameIds = action.payload.gameIds;
      state.repairMinWeight = action.payload.minWeight;
    },
    closeRepair: (state) => {
      state.repairGameIds = null;
      state.repairMinWeight = null;
    },
    // Play a position from one of your own games out against the engine.
    openPlayOut: (state, action: PayloadAction<PlayOutRequest>) => {
      state.playOut = action.payload;
    },
    closePlayOut: (state) => {
      state.playOut = null;
    },
  },
});
export const {
  setGameIdForModal,
  clearOpenCorrect,
  setMoveRefForModal,
  refreshGameForModal,
  setShowImportGames,
  setShowManageGroups,
  setSelectionMode,
  toggleGameSelected,
  setSelectedGameIds,
  clearSelection,
  openRepairAt,
  closeRepair,
  openPlayOut,
  closePlayOut,
} = slice.actions;
export default slice.reducer;
